import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useTheme } from 'styled-components';

import { getAnalyticsData, getUserDetails } from '../../api/Optimise';
import { AppContext } from '../../context/AppContext';
import withPageTitle from '../../hocs/withPageTitle';
import { useOnDateChange } from '../../hooks';
import { AnalyticsDataItem, OptimiseUserDetails } from '../../types/optimise';
import ExportChart from '../../ui/Dashboard/Savings/ExportChart';
import ImportChart from '../../ui/Dashboard/Savings/ImportChart';
import SavingsSummary from '../../ui/Dashboard/Savings/SavingsSummary';
import TariffKey from '../../ui/Dashboard/Savings/TariffKey';
import InfoModal from '../../ui/Modal/InfoModal';
import WarningPanel from '../../ui/WarningPanel';
import { UserSub } from '../../utils/apiRequest';
import { getSavingsTotal, transformAnalyticsDataForChart } from '../../utils/savingsUtils';

interface AppContextType {
  userDetails: OptimiseUserDetails | null;
  analyticsStartDate: string | null;
  userSub: UserSub;
  setDatePickerWarning: (warning: React.ReactNode | undefined) => void;
  setSelectedDate: (date: Date) => void;
}

const timeUntilRefresh: number = 5 * 60 * 1000;

const SavingsContainer = () => {
  const history = useHistory();
  const { language } = useTheme();
  const [fetchedAnalyticsData, setFetchedAnalyticsData] = useState<boolean>(false);
  const [fetchedLatestAnalyticsData, setFetchedLatestAnalyticsData] = useState<boolean>(false);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsDataItem[] | null>(null);
  const [latestItem, setLatestItem] = useState<AnalyticsDataItem | null>(null);
  const [formattedDate, setFormattedDate] = useState<string>(moment().subtract(1, 'days').format('YYYY-MM-DD'));
  const [showNoDataModal, setShowNoDataModal] = useState<boolean>(false);

  const { userDetails, analyticsStartDate, userSub, setDatePickerWarning, setSelectedDate } =
    useContext<AppContextType>(AppContext);

  const startDateIsToday: boolean = !!analyticsStartDate ? moment(analyticsStartDate).isSame(moment(), 'day') : true;

  const fetchAnalyticsData = useCallback(
    async (date: string) => {
      try {
        const data = await getAnalyticsData(date, userSub);
        setAnalyticsData(data);
      } catch (error: any) {
        if (error.status === 404) {
          setAnalyticsData([]);
        }
        console.error('Error fetching analytics data:', error);
      } finally {
        setFetchedAnalyticsData(true);
      }
    },
    [userSub]
  );

  const fetchLatestItem = useCallback(async () => {
    const user = await getUserDetails(userSub);
    const end: string = moment.utc(user?.analytics_data_range.end_date).format('YYYY-MM-DD');
    try {
      const data: AnalyticsDataItem[] = await getAnalyticsData(end, userSub);
      if (!!data) {
        setLatestItem(data[data.length - 1]);
      }
    } catch (error: any) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setFetchedLatestAnalyticsData(false);
    }
  }, [userSub]);

  useEffect(() => {
    void fetchAnalyticsData(formattedDate);
    void fetchLatestItem();

    // Set up periodic refresh (e.g., every 5 minutes)
    const refreshInterval: NodeJS.Timeout = setInterval(() => {
      void fetchAnalyticsData(formattedDate);
      void fetchLatestItem();
    }, timeUntilRefresh);

    return () => clearInterval(refreshInterval);
  }, [formattedDate, fetchAnalyticsData]);

  const { savings, savingsChartData } = useMemo((): {
    savings?: number;
    savingsChartData?: any[];
  } => {
    if (!analyticsData) {
      return {};
    }
    return {
      savingsChartData: transformAnalyticsDataForChart(analyticsData),
      savings: getSavingsTotal(analyticsData),
    };
  }, [analyticsData, userDetails]);

  useEffect(() => {
    if (startDateIsToday) {
      setDatePickerWarning(
        <WarningPanel
          title="Not enough data to show your Savings."
          body={`Please wait 24 hours for the ${language.appName} calculations to be made.`}
          linkText={`Why does ${language.appName} not have my data yet?`}
          linkAction={() => setShowNoDataModal(true)}
        />
      );
    } else {
      setDatePickerWarning(undefined);
    }
  }, [startDateIsToday, language.appName]);

  const onSelectDate = useCallback(
    (date: Date): void => {
      const formattedDate: string = moment(date).format('YYYY-MM-DD');
      if (userDetails) {
        // NOTE: this stops the savings page being able to have a date before the range
        // when navigating to it after setting a different date.
        if (moment(userDetails.analytics_data_range.start_date) > moment(date)) {
          setSelectedDate(moment(userDetails.analytics_data_range.start_date).toDate());
          return;
        }
      }
      history.push(`/app/savings/${moment(date).unix()}`);
      setFormattedDate(formattedDate);
    },
    [history, userDetails]
  );

  useOnDateChange(onSelectDate);

  return (
    <>
      <SavingsSummary
        savings={savings}
        total={latestItem?.running_total_saving_against_dumb_battery}
        fetchedAnalyticsData={fetchedAnalyticsData}
        fetchedLatestAnalyticsData={fetchedLatestAnalyticsData}
        userStartDate={userDetails?.analytics_data_range.start_date}
      />
      {/* @ts-ignore */}
      <ExportChart chartData={savingsChartData} date={formattedDate} />
      {/* @ts-ignore */}
      <ImportChart chartData={savingsChartData} date={formattedDate} />
      <TariffKey savingsChartData={savingsChartData} />

      <InfoModal
        show={showNoDataModal}
        onClose={() => setShowNoDataModal(false)}
        infoContent={
          <div>
            Calculating relative savings can be a complex and time-consuming process. For this reason,{' '}
            {language.appName} performs these calculations once a day (overnight) to provide a summary of savings from
            the previous day. While this approach may change in the future, a daily summary is currently sufficient to
            show how much was saved using {language.appName}. <br />
            <br />
            So, when you first sign up, you may need to wait up to 24 hours for daily savings to be available.
          </div>
        }
      />
    </>
  );
};

export default function WrappedHelpContainer(): React.ReactElement {
  const { language } = useTheme();
  return withPageTitle(`${language.appName} Savings`, SavingsContainer)();
}
